import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Heart } from "lucide-react";
import Header from "@/components/site/Header";
import api from "@/lib/api";

const MAX_POLLS = 6;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("error"); return; }
    if (attempt >= MAX_POLLS) { setState("pending"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setAmount(data.amount);
      if (data.payment_status === "paid") { setState("paid"); return; }
      setTimeout(() => poll(attempt + 1), 1500);
    } catch { setState("error"); }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="bg-imi-bg text-imi-text min-h-screen">
      <Header />
      <main className="pt-28 pb-24 min-h-[70vh] flex items-center justify-center px-6">
        <div className="max-w-lg text-center" data-testid="payment-success">
          {state === "checking" && (
            <><Loader2 className="animate-spin text-imi-gold mx-auto mb-6" size={48} />
              <h1 className="font-serif text-3xl text-imi-text">Confirming your donation…</h1></>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="text-imi-gold mx-auto mb-6" size={64} />
              <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-4">Thank you</p>
              <h1 className="font-serif text-4xl md:text-6xl leading-tight">Your gift moves a <span className="italic gold-gradient-text">story forward</span>.</h1>
              <p className="mt-6 text-imi-text/60 text-lg">{amount ? `We've received your $${amount} donation.` : "Your donation was received."} You just helped fund the next production.</p>
              <Link to="/" className="mt-10 inline-flex items-center gap-3 bg-imi-gold text-imi-bg px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors">
                Back to site <Heart size={16} />
              </Link>
            </>
          )}
          {state === "pending" && (
            <><Loader2 className="text-imi-gold mx-auto mb-6" size={48} />
              <h1 className="font-serif text-3xl">Your payment is processing</h1>
              <p className="mt-4 text-imi-text/60">It may take a moment. You&rsquo;ll receive a Stripe receipt by email.</p>
              <Link to="/" className="mt-8 inline-block text-imi-gold uppercase tracking-[0.15em] text-sm">Back to site</Link></>
          )}
          {state === "error" && (
            <><XCircle className="text-red-400 mx-auto mb-6" size={48} />
              <h1 className="font-serif text-3xl">We couldn&rsquo;t confirm this payment</h1>
              <Link to="/#support" className="mt-8 inline-block text-imi-gold uppercase tracking-[0.15em] text-sm">Try again</Link></>
          )}
        </div>
      </main>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="bg-imi-bg text-imi-text min-h-screen">
      <Header />
      <main className="pt-28 pb-24 min-h-[70vh] flex items-center justify-center px-6">
        <div className="max-w-lg text-center" data-testid="payment-cancel">
          <XCircle className="text-imi-text/40 mx-auto mb-6" size={56} />
          <h1 className="font-serif text-4xl">Donation canceled</h1>
          <p className="mt-4 text-imi-text/60 text-lg">No charge was made. Whenever you&rsquo;re ready, we&rsquo;d be grateful for your support.</p>
          <Link to="/#support" data-testid="try-again" className="mt-10 inline-flex items-center gap-3 bg-imi-gold text-imi-bg px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors">
            Return to donate
          </Link>
        </div>
      </main>
    </div>
  );
}
